import {
    DI,
    GlobalEventsMap,
    IEventManagerWithAsync,
    inject,
    LogicEvents,
    Module,
    ReelConstants,
    ReelsEvents,
    SoundManager,
    SymbolPosition,
    UIDisableReason
} from '@gongxigames/framework/slot';
import {GameLogic} from 'src/server/GameLogic';
import {GameFlowModuleEvents} from 'src/win/GameFlowModuleEvents';
import {RandomWildView} from 'src/randomWild/RandomWildView';
import {WolfHavenSpin} from 'com/gongxigames/math/wolfhaven/engine/WolfHavenSpin';
import {WolfHaven} from 'com/gongxigames/math/wolfhaven/engine/WolfHaven';
import {RandomWildEvents} from 'src/randomWild/RandomWildEvents';
import {Action} from 'com/gongxigames/math/core/game/enums/Action';
import {GameReelConstants} from 'src/gameReels/GameReelConstants';

export interface RandomWildData {
    fromPos: SymbolPosition;
    toPos: SymbolPosition;
    reel: number;
    row: number;
    dimensionFrom: number;
    dimensionTo: number;
}

export class RandomWildModule extends Module<RandomWildView> {
    public static readonly NAME: symbol = Symbol('RandomWildModule');
    @inject(DI.SoundManager)
    protected _soundManager!: SoundManager;
    protected _randomWildsData!: RandomWildData[];

    protected setupViews(): void {
        this.setView(RandomWildView.NAME);
    }

    protected setupStates(): void {
    }

    protected setupEvents(): void {
        super.setupEvents();
        this.on(GameFlowModuleEvents.START_RANDOM_WILD_FEATURE, this.onStartRandomWildFeature);
        this.on(ReelsEvents.REELS_SPINNING_STARTED, this.onReelSpinningStarted);
        this.on(LogicEvents.SPIN_RESPONSE, this.onSpinResponseReceived);
        this.on(LogicEvents.FREE_SPIN_RESPONSE, this.onSpinResponseReceived);
        this.onView(RandomWildView.WILD_LANDED, this.onWildLanded);
        this.onView(RandomWildView.SHOULD_START_DARKENING, this.onShouldStarDarkening);
        this.onView(RandomWildView.START_NEXT_FLIGHT, this.onStartNextFlight);
        this.onView(RandomWildView.ACTIVATOR_WIN_FINISHED, this.onActivatorWinFinished);
    }

    protected onSpinResponseReceived(): void {
        const logic = this.logic as GameLogic;

        this._randomWildsData = [];

        if (logic.isRandomWildFeature()) {
            this.setupRandomWildsPositions();
        }
    }

    protected onActivatorWinFinished(): void {
        this.eventManager.emit(RandomWildEvents.ACTIVATOR_WIN_FINISHED);
    }

    protected setupRandomWildsPositions(): void {
        const spin = (this.logic.spinData as WolfHavenSpin);
        const symbolIds = spin.symbolIds;
        const positions = (this.logic as GameLogic).getRandomWildPositions();
        const activatorRow = symbolIds[ReelConstants.REELS_AMOUNT - 1].indexOf(WolfHaven.Activator);
        const dimension = spin.getDimension();
        const fromPos = this.getSymbolPosition(ReelConstants.REELS_AMOUNT - 1, activatorRow, dimension[ReelConstants.REELS_AMOUNT - 1]);
        const from = {
            x: fromPos.x + GameReelConstants.DIMENSION_SYMBOL_SLOT_SIZES[dimension[ReelConstants.REELS_AMOUNT - 1]].width / 2,
            y: fromPos.y + GameReelConstants.DIMENSION_SYMBOL_SLOT_SIZES[dimension[ReelConstants.REELS_AMOUNT - 1]].height / 2
        } as SymbolPosition;

        positions.forEach((pos) => {
            const toPos = this.getSymbolPosition(pos.reel, pos.row, dimension[pos.reel]);
            const to = {
                x: toPos.x + GameReelConstants.DIMENSION_SYMBOL_SLOT_SIZES[dimension[pos.reel]].width / 2,
                y: toPos.y + GameReelConstants.DIMENSION_SYMBOL_SLOT_SIZES[dimension[pos.reel]].height / 2
            } as SymbolPosition;
            const finalPos = {
                x: to.x - from.x,
                y: to.y - from.y
            } as SymbolPosition;
            const randomWildData: RandomWildData = {
                fromPos: from,
                toPos: finalPos,
                reel: pos.reel,
                row: pos.row,
                dimensionFrom: dimension[ReelConstants.REELS_AMOUNT - 1],
                dimensionTo: dimension[pos.reel]
            };

            this._randomWildsData.push(randomWildData);
        });
    }

    protected onStartRandomWildFeature(): void {
        this.duckMainGameBGM();
        this.updateAwardedWildsCount();
        this.view.playActivationSound();
        this.view.playWinAnimation(this._randomWildsData.pop()!);
        this.eventManager.emit(RandomWildEvents.ACTIVATOR_WIN_STARTED);
    }

    protected duckMainGameBGM(): void {
        const baseBGM = this._soundManager.getMusic('maingame');
        const fgBGM = this._soundManager.getMusic('freespin');

        if (baseBGM && baseBGM.isPlaying) {
            baseBGM.volume = 0.25;
        } else if (fgBGM && fgBGM.isPlaying) {
            fgBGM.volume = 0.25;
        }
    }

    protected unDuckMainGameBGM(): void {
        const baseBGM = this._soundManager.getMusic('maingame');
        const fgBGM = this._soundManager.getMusic('freespin');

        if (baseBGM && baseBGM.isPlaying) {
            baseBGM.volume = 1;
        } else if (fgBGM && fgBGM.isPlaying) {
            fgBGM.volume = 1;
        }
    }

    protected updateAwardedWildsCount(): void {
        const spin = (this.logic.spinData as WolfHavenSpin);
        const symbolIds = spin.symbolIds;
        const positions = (this.logic as GameLogic).getRandomWildPositions();

        symbolIds.forEach((reel, reelIndex) => {
            reel.forEach((symbol, rowIndex) => {
                if (symbol === WolfHaven.Wild) {
                    if (!positions.some(pos => pos.reel === reelIndex && pos.row === rowIndex)) {
                        this.view.alreadyAwardedWilds++;
                    }
                }
            });
        });
    }

    protected getSymbolPosition(reel: number, row: number, dimension: number = GameReelConstants.DEFAULT_DIMENSION): SymbolPosition {
        const symsPositions = ReelConstants.REELS[reel].symsPositions[0];

        return {
            x: symsPositions.x,
            y: symsPositions.y + GameReelConstants.DIMENSION_SYMBOL_SLOT_SIZES[dimension].height * row
        };
    }

    protected onWildLanded(eventID: string | symbol, reel: number, row: number): void {
        reel && this.eventManager.emit(RandomWildEvents.WILD_LANDED, reel, row);
    }

    protected onStartNextFlight(): void {
        if (this._randomWildsData.length === 0) {
            this.onRandomWildFeatureFinished();
        } else {
            const randomWildData = this._randomWildsData.pop();

            this.view.playActivationAnimation(randomWildData!);
            this.view.playMoveAnimation(randomWildData!);
        }
    }

    protected onShouldStarDarkening(): void {
        this.eventManager.emit(RandomWildEvents.SHOULD_START_DARKENING);
    }

    protected onRandomWildFeatureFinished(): void {
        this.unDuckMainGameBGM();
        !this.checkIfFreeSpinsTriggerOrRetrigger()
        && (this.eventManager as IEventManagerWithAsync<GlobalEventsMap>).emit('General.UI.ENABLE', UIDisableReason.TRIGGERING_FEATURE);
        this.view.reset();
        this.eventManager.emit(RandomWildEvents.RANDOM_WILD_FEATURE_FINISHED);
    }

    protected onReelSpinningStarted(): void {
        this.view.reset();
    }

    protected checkIfFreeSpinsTriggerOrRetrigger(): boolean {
        return (this.logic.previousAction === Action.SPIN
            && this.logic.nextAction === Action.FREESPIN) || this.logic.isRetriggerWon();
    }
}
